import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Text, VStack, HStack, Button, Flex, Icon, IconButton,
  FormControl, FormLabel, Input, Select, Textarea, Checkbox,
  Tabs, TabList, Tab, TabPanels, TabPanel,
  SimpleGrid, Badge, Divider, InputGroup, InputLeftElement,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaCheckCircle, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

/* ─── DropZone ─────────────────────────────────────────────────────────── */
const DropZone = ({ label, files, setFiles, previews, setPreviews, fileNames, setFileNames }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.[0]) processFiles(e.dataTransfer.files); };
  const handleChange = (e) => { e.preventDefault(); if (e.target.files?.[0]) processFiles(e.target.files); };
  const processFiles = (fileList) => {
    const arr = Array.from(fileList);
    setFiles(prev => [...prev, ...arr]);
    setPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setFileNames(prev => [...prev, ...arr.map(f => f.name)]);
  };
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setFileNames(prev => prev.filter((_, i) => i !== index));
  };
  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="600" color="gray.600">{label}</FormLabel>
      <Box onClick={() => inputRef.current.click()} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
        p={6} borderWidth={2} borderStyle="dashed" borderRadius="lg" bg={dragActive ? 'blue.50' : 'gray.50'}
        borderColor={dragActive ? 'blue.400' : 'gray.200'} _hover={{ borderColor: 'blue.300' }}
        cursor="pointer" textAlign="center" transition="all 0.2s">
        <VStack spacing={2}>
          <Icon as={FaCloudUploadAlt} boxSize={8} color="blue.400" />
          <Text fontSize="sm" color="gray.500">{dragActive ? 'Drop files here' : 'Drag & drop or click to browse'}</Text>
          <Text fontSize="xs" color="gray.400">PDF, JPG, PNG</Text>
        </VStack>
        <input ref={inputRef} type="file" multiple accept="application/pdf,image/*" onChange={handleChange} style={{ display: 'none' }} />
      </Box>
      {previews.length > 0 && (
        <VStack spacing={1} mt={2} align="stretch">
          {previews.map((_, i) => (
            <Flex key={i} justify="space-between" align="center" p={2} bg="white" border="1px solid" borderColor="gray.100" borderRadius="md">
              <HStack><Icon as={FaCloudUploadAlt} color="green.500" boxSize={3} /><Text fontSize="xs" fontWeight="500">{fileNames[i]}</Text></HStack>
              <IconButton icon={<FaTimes />} size="xs" variant="ghost" colorScheme="red" onClick={(e) => { e.stopPropagation(); removeFile(i); }} aria-label="Remove" />
            </Flex>
          ))}
        </VStack>
      )}
    </FormControl>
  );
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */
async function generateDrawCode() {
  try {
    let allCodes = [], from = 0;
    while (true) {
      const { data } = await supabase.from('submissions').select('draw_code').not('draw_code', 'is', null).range(from, from + 999);
      if (!data || data.length === 0) break;
      allCodes = allCodes.concat(data);
      if (data.length < 1000) break;
      from += 1000;
    }
    let max = 1000;
    for (const row of allCodes) { const n = parseInt(row.draw_code, 10); if (!isNaN(n) && n > max) max = n; }
    return String(max + 1);
  } catch { return String(Date.now()).slice(-8); }
}

async function uploadFiles(files, type) {
  const urls = [];
  for (const file of files) {
    const ext = file.name.split('.').pop();
    const name = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
    const path = `${type}/${name}`;
    const { error } = await supabase.storage.from('uploads').upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: true });
    if (error) { console.error('Upload error:', error); continue; }
    const { data: { publicUrl } } = supabase.storage.from('uploads').getPublicUrl(path);
    urls.push(publicUrl);
  }
  return urls;
}

const SPECIMEN_TYPES = [
  'Blood - General','Blue Top tube','Endocervical swab','Gray Top tube - Sodium Flouride',
  'GREEN-TOP TUBE','Lavender Top Tube','Light Blue Tube','Pink Top tube',
  'Plain Red Top Tube','Serum Separator tube','Urine Cup','Urine Toxicology',
  'Vaginal swab','Whole Blood','Wound','Yellow Top Tube - ACD A or B',
  'Nasal Swab (Default)','Oral Swab','Stool,Fresh','Swab',
];

/* ─── Main Component ──────────────────────────────────────────────────── */
export default function FacilityOrderEntry() {
  const { facility, facilityId } = useOutletContext();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Form
  const [form, setForm] = useState({
    patientFirstName: '', patientLastName: '', patientDOB: '', patientGender: '',
    patientEmail: '', patientStreet: '', patientCity: '', patientState: '', patientZip: '',
    insuranceId: '', insuranceCompany: '', policyNumber: '', groupNumber: '', subscriberId: '', relationshipToSubscriber: 'Self',
    isSelfPay: false,
    doctorName: '', doctorPhone: '', doctorEmail: '',
    phlebotomistId: '',
    bloodCollectionDate: '', bloodCollectionTime: '',
    specialInstructions: '',
  });

  // Tests
  const [testTypes, setTestTypes] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);
  const [showTestDropdown, setShowTestDropdown] = useState(false);

  // Specimens
  const [specimens, setSpecimens] = useState([{ quantity: 1, type: '' }]);

  // Diagnosis
  const [diagnoses, setDiagnoses] = useState(['']);

  // Insurance lookups
  const [insurances, setInsurances] = useState([]);

  // Phlebotomist lookups
  const [phlebotomists, setPhlebotomists] = useState([]);
  const [phlebSearch, setPhlebSearch] = useState('');
  const [showPhlebDropdown, setShowPhlebDropdown] = useState(false);
  const [selectedPhlebotomist, setSelectedPhlebotomist] = useState(null);

  // Files
  const [scriptFiles, setScriptFiles] = useState([]);
  const [scriptPreviews, setScriptPreviews] = useState([]);
  const [scriptFileNames, setScriptFileNames] = useState([]);
  const [insuranceFiles, setInsuranceFiles] = useState([]);
  const [insurancePreviews, setInsurancePreviews] = useState([]);
  const [insuranceFileNames, setInsuranceFileNames] = useState([]);
  const [idFiles, setIdFiles] = useState([]);
  const [idPreviews, setIdPreviews] = useState([]);
  const [idFileNames, setIdFileNames] = useState([]);

  // Fetch lookups
  useEffect(() => {
    const fetchData = async () => {
      const [testsRes, panelsRes, insRes, phlebRes] = await Promise.all([
        supabase.from('tests').select('id, name, code, description, cash_price'),
        supabase.from('panels').select('id, name, code, description, cash_price'),
        supabase.from('insurances').select('id, name').order('name'),
        supabase.from('phlebotomist_profiles').select('id, full_name, company_name, email, phone').order('full_name'),
      ]);
      const tests = (testsRes.data || []).map(t => ({ ...t, isPanel: false }));
      const panels = (panelsRes.data || []).map(p => ({ ...p, isPanel: true }));
      setTestTypes([...tests, ...panels]);
      setInsurances(insRes.data || []);
      setPhlebotomists(phlebRes.data || []);
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Test search
  useEffect(() => {
    if (!testSearch.trim()) { setFilteredTests([]); setShowTestDropdown(false); return; }
    const q = testSearch.toLowerCase();
    const selectedIds = new Set(selectedTests.map(t => t.id));
    const matches = testTypes.filter(t =>
      !selectedIds.has(t.id) && (
        (t.name || '').toLowerCase().includes(q) ||
        (t.code || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
      )
    ).slice(0, 15);
    setFilteredTests(matches);
    setShowTestDropdown(matches.length > 0);
  }, [testSearch, testTypes, selectedTests]);

  const addTest = (test) => {
    setSelectedTests(prev => [...prev, test]);
    setTestSearch('');
    setShowTestDropdown(false);
  };

  const removeTest = (id) => setSelectedTests(prev => prev.filter(t => t.id !== id));

  // Phleb search
  const filteredPhlebs = phlebotomists.filter(p =>
    phlebSearch && ((p.full_name || '').toLowerCase().includes(phlebSearch.toLowerCase()) ||
    (p.company_name || '').toLowerCase().includes(phlebSearch.toLowerCase()))
  ).slice(0, 10);

  // Validation
  const validateTab = (tab) => {
    if (tab === 0) {
      if (!form.patientLastName.trim() || !form.patientFirstName.trim()) {
        toast({ title: 'Patient name is required', status: 'warning', duration: 3000 }); return false;
      }
    }
    if (tab === 1) {
      if (selectedTests.length === 0) {
        toast({ title: 'At least one test is required', status: 'warning', duration: 3000 }); return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateTab(activeTab)) setActiveTab(prev => Math.min(prev + 1, 2));
  };

  const handleSubmit = async () => {
    if (!validateTab(0) || !validateTab(1)) return;
    if (!facilityId) { toast({ title: 'Facility error', status: 'error', duration: 3000 }); return; }

    setSubmitting(true);
    try {
      // Upload files
      const [scriptUrls, insuranceUrls, idUrls] = await Promise.all([
        uploadFiles(scriptFiles, 'script'),
        uploadFiles(insuranceFiles, 'insurance'),
        uploadFiles(idFiles, 'patientId'),
      ]);

      const drawCode = await generateDrawCode();
      const patientName = `${form.patientLastName.trim()}, ${form.patientFirstName.trim()}`;
      const address = [form.patientStreet, form.patientCity, form.patientState, form.patientZip].filter(Boolean).join(', ');

      const collectionTime = form.bloodCollectionDate
        ? `${form.bloodCollectionDate}${form.bloodCollectionTime ? 'T' + form.bloodCollectionTime : 'T00:00'}:00`
        : null;

      const dbData = {
        patient_name: patientName,
        patient_email: form.patientEmail.trim() || null,
        patient_dob: form.patientDOB || null,
        patient_gender: form.patientGender || null,
        patient_address: address || null,
        patient_street: form.patientStreet.trim() || null,
        patient_city: form.patientCity.trim() || null,
        patient_state: form.patientState.trim() || null,
        patient_zip: form.patientZip.trim() || null,
        doctor_name: form.doctorName.trim() || null,
        doctor_phone: form.doctorPhone.trim() || null,
        doctor_email: form.doctorEmail.trim() || null,
        is_self_pay: form.isSelfPay,
        insurance_company: form.isSelfPay ? null : (insurances.find(i => i.id === form.insuranceId)?.name || null),
        insurance_id: form.isSelfPay ? null : (form.insuranceId || null),
        insurance_policy_number: form.isSelfPay ? null : (form.policyNumber.trim() || null),
        policy_number: form.isSelfPay ? null : (form.policyNumber.trim() || null),
        group_number: form.isSelfPay ? null : (form.groupNumber.trim() || null),
        subscriber_id: form.isSelfPay ? null : (form.subscriberId.trim() || null),
        relationship_to_subscriber: form.isSelfPay ? null : form.relationshipToSubscriber,
        tests_on_the_script: selectedTests.map(t => ({ id: t.id, name: t.name, code: t.code, description: t.description, cash_price: t.cash_price })),
        blood_collection_time: collectionTime,
        specimen_type: JSON.stringify(specimens.filter(s => s.type)),
        diagnosis: diagnoses.filter(Boolean).join('; '),
        special_instructions: form.specialInstructions.trim() || null,
        phlebotomist_id: selectedPhlebotomist?.id || null,
        phlebotomist_name: selectedPhlebotomist?.company_name || selectedPhlebotomist?.full_name || null,
        phlebotomist_email: selectedPhlebotomist?.email || null,
        facility_id: facilityId,
        client_name: facility?.name || null,
        created_by_user: `FAC-${facility?.name || 'Unknown'}`,
        script_image: scriptUrls.length > 0 ? scriptUrls : null,
        insurance_card_image: insuranceUrls.length > 0 ? insuranceUrls : null,
        patient_id_image: idUrls.length > 0 ? idUrls : null,
        draw_code: drawCode,
        status: 'pending',
        submitted_at: new Date().toISOString(),
      };

      const { data, error } = await supabase.from('submissions').insert([dbData]).select('id, draw_code').single();
      if (error) throw error;

      setSuccessData({ id: data.id, drawCode: data.draw_code, patientName });
    } catch (err) {
      toast({ title: 'Submission failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (successData) {
    return (
      <Box textAlign="center" py={16}>
        <Icon as={FaCheckCircle} boxSize={20} color="green.400" mb={4} />
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" mb={2}>Order Submitted Successfully!</Text>
        <Text color="gray.500" mb={1}>Patient: {successData.patientName}</Text>
        <Badge fontSize="lg" colorScheme="blue" px={4} py={2} borderRadius="full" mb={6}>Accession #: {successData.drawCode}</Badge>
        <HStack spacing={4} justify="center">
          <Button variant="outline" onClick={() => navigate(`/facility/${facilityId}`)}>Back to Dashboard</Button>
          <Button colorScheme="blue" onClick={() => { setSuccessData(null); setForm({ patientFirstName: '', patientLastName: '', patientDOB: '', patientGender: '', patientEmail: '', patientStreet: '', patientCity: '', patientState: '', patientZip: '', insuranceId: '', insuranceCompany: '', policyNumber: '', groupNumber: '', subscriberId: '', relationshipToSubscriber: 'Self', isSelfPay: false, doctorName: '', doctorPhone: '', doctorEmail: '', phlebotomistId: '', bloodCollectionDate: '', bloodCollectionTime: '', specialInstructions: '', statTest: false }); setSelectedTests([]); setSpecimens([{ quantity: 1, type: '' }]); setDiagnoses(['']); setScriptFiles([]); setScriptPreviews([]); setScriptFileNames([]); setInsuranceFiles([]); setInsurancePreviews([]); setInsuranceFileNames([]); setIdFiles([]); setIdPreviews([]); setIdFileNames([]); setActiveTab(0); }}>Create Another Order</Button>
        </HStack>
      </Box>
    );
  }

  return (
    <Box>
      <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue" isLazy>
        <TabList mb={4} borderBottom="2px" borderColor="gray.100">
          <Tab fontWeight="600" fontSize="sm" py={3}>1. Patient / Insurance</Tab>
          <Tab fontWeight="600" fontSize="sm" py={3}>2. Order Information</Tab>
          <Tab fontWeight="600" fontSize="sm" py={3}>3. Documents & Submit</Tab>
        </TabList>

        <TabPanels>
          {/* ─── TAB 1: Patient / Insurance ──────────────────────── */}
          <TabPanel px={0}>
            <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.100" p={6}>
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={4}>Patient Information</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Last Name</FormLabel>
                  <Input name="patientLastName" value={form.patientLastName} onChange={handleChange} placeholder="Doe" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">First Name</FormLabel>
                  <Input name="patientFirstName" value={form.patientFirstName} onChange={handleChange} placeholder="John" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Date of Birth</FormLabel>
                  <Input name="patientDOB" type="date" value={form.patientDOB} onChange={handleChange} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Gender</FormLabel>
                  <Select name="patientGender" value={form.patientGender} onChange={handleChange} placeholder="Select">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input name="patientEmail" type="email" value={form.patientEmail} onChange={handleChange} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Client / Facility</FormLabel>
                  <Input value={facility?.name || ''} isReadOnly bg="gray.50" />
                </FormControl>
                <FormControl gridColumn={{ md: 'span 2' }}>
                  <FormLabel fontSize="sm">Street Address</FormLabel>
                  <Input name="patientStreet" value={form.patientStreet} onChange={handleChange} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">City</FormLabel>
                  <Input name="patientCity" value={form.patientCity} onChange={handleChange} />
                </FormControl>
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">State</FormLabel>
                    <Input name="patientState" value={form.patientState} onChange={handleChange} maxW="100px" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Zip</FormLabel>
                    <Input name="patientZip" value={form.patientZip} onChange={handleChange} maxW="120px" />
                  </FormControl>
                </HStack>
              </SimpleGrid>

              <Divider my={6} />

              <HStack mb={4} spacing={4}>
                <Text fontWeight="700" fontSize="md" color="blue.700">Insurance</Text>
                <Checkbox name="isSelfPay" isChecked={form.isSelfPay} onChange={handleChange} colorScheme="green">
                  <Text fontSize="sm" fontWeight="500">Self Pay</Text>
                </Checkbox>
              </HStack>

              {!form.isSelfPay && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Insurance Provider</FormLabel>
                    <Select name="insuranceId" value={form.insuranceId} onChange={handleChange} placeholder="Select insurance">
                      {insurances.map(ins => <option key={ins.id} value={ins.id}>{ins.name}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Policy Number</FormLabel>
                    <Input name="policyNumber" value={form.policyNumber} onChange={handleChange} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Group Number</FormLabel>
                    <Input name="groupNumber" value={form.groupNumber} onChange={handleChange} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Member / Subscriber ID</FormLabel>
                    <Input name="subscriberId" value={form.subscriberId} onChange={handleChange} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Relationship to Subscriber</FormLabel>
                    <Select name="relationshipToSubscriber" value={form.relationshipToSubscriber} onChange={handleChange}>
                      <option value="Self">Self</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
              )}
            </Box>
            <Flex justify="flex-end" mt={4}>
              <Button colorScheme="blue" onClick={handleNext}>Next: Order Information</Button>
            </Flex>
          </TabPanel>

          {/* ─── TAB 2: Order Information ─────────────────────────── */}
          <TabPanel px={0}>
            <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.100" p={6}>
              {/* Doctor */}
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={4}>Doctor Information</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                <FormControl>
                  <FormLabel fontSize="sm">Doctor Name</FormLabel>
                  <Input name="doctorName" value={form.doctorName} onChange={handleChange} placeholder="Dr. Smith" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Doctor Phone</FormLabel>
                  <Input name="doctorPhone" value={form.doctorPhone} onChange={handleChange} placeholder="(555) 123-4567" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Doctor Email</FormLabel>
                  <Input name="doctorEmail" type="email" value={form.doctorEmail} onChange={handleChange} placeholder="doctor@clinic.com" />
                </FormControl>
              </SimpleGrid>

              <Divider my={4} />

              {/* Phlebotomist / Phleb */}
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={4}>Phlebotomist / Phlebotomist</Text>
              <FormControl mb={4} position="relative">
                <InputGroup>
                  <InputLeftElement><SearchIcon color="gray.400" /></InputLeftElement>
                  <Input placeholder="Search phlebotomist..." value={phlebSearch} onChange={(e) => { setPhlebSearch(e.target.value); setShowPhlebDropdown(true); }} onFocus={() => setShowPhlebDropdown(true)} onBlur={() => setTimeout(() => setShowPhlebDropdown(false), 200)} />
                </InputGroup>
                {selectedPhlebotomist && (
                  <Badge mt={1} colorScheme="purple" fontSize="xs" px={2} py={1}>
                    {selectedPhlebotomist.company_name || selectedPhlebotomist.full_name}
                    <IconButton icon={<CloseIcon />} size="xs" variant="ghost" ml={1} onClick={() => { setSelectedPhlebotomist(null); setPhlebSearch(''); }} />
                  </Badge>
                )}
                {showPhlebDropdown && filteredPhlebs.length > 0 && (
                  <Box position="absolute" zIndex={10} bg="white" w="full" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" mt={1}>
                    {filteredPhlebs.map(p => (
                      <Box key={p.id} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }}
                        onClick={() => { setSelectedPhlebotomist(p); setPhlebSearch(p.company_name || p.full_name); setShowPhlebDropdown(false); }}>
                        <Text fontSize="sm" fontWeight="500">{p.company_name || p.full_name}</Text>
                        <Text fontSize="xs" color="gray.500">{p.email}</Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                <FormControl>
                  <FormLabel fontSize="sm">Collection Date</FormLabel>
                  <Input name="bloodCollectionDate" type="date" value={form.bloodCollectionDate} onChange={handleChange} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Collection Time</FormLabel>
                  <Input name="bloodCollectionTime" type="time" value={form.bloodCollectionTime} onChange={handleChange} />
                </FormControl>
              </SimpleGrid>

              <Divider my={4} />

              {/* Tests */}
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={3}>Tests *</Text>
              <FormControl position="relative" mb={3}>
                <InputGroup>
                  <InputLeftElement><SearchIcon color="gray.400" /></InputLeftElement>
                  <Input placeholder="Search tests by name or code..." value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)} onFocus={() => testSearch && setShowTestDropdown(true)}
                    onBlur={() => setTimeout(() => setShowTestDropdown(false), 200)} />
                </InputGroup>
                {showTestDropdown && filteredTests.length > 0 && (
                  <Box position="absolute" zIndex={10} bg="white" w="full" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="lg" maxH="250px" overflowY="auto" mt={1}>
                    {filteredTests.map(t => (
                      <Box key={t.id} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => addTest(t)}>
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="500">{t.name}</Text>
                          {t.isPanel && <Badge colorScheme="purple" fontSize="2xs">Panel</Badge>}
                        </HStack>
                        <Text fontSize="xs" color="gray.500">{t.code} {t.description ? `— ${t.description.slice(0, 60)}...` : ''}</Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </FormControl>
              <Flex flexWrap="wrap" gap={2} mb={4}>
                {selectedTests.map(t => (
                  <Badge key={t.id} colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="xs">
                    {t.name} {t.code ? `(${t.code})` : ''}
                    <IconButton icon={<CloseIcon />} size="xs" variant="ghost" ml={1} onClick={() => removeTest(t.id)} />
                  </Badge>
                ))}
                {selectedTests.length === 0 && <Text fontSize="sm" color="gray.400">No tests selected</Text>}
              </Flex>

              <Divider my={4} />

              {/* Specimen */}
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={3}>Specimen</Text>
              {specimens.map((spec, i) => (
                <HStack key={i} spacing={3} mb={2}>
                  <Select w="80px" value={spec.quantity} onChange={(e) => { const s = [...specimens]; s[i].quantity = Number(e.target.value); setSpecimens(s); }}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </Select>
                  <Select flex={1} value={spec.type} placeholder="Select specimen type" onChange={(e) => { const s = [...specimens]; s[i].type = e.target.value; setSpecimens(s); }}>
                    {SPECIMEN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                  {specimens.length > 1 && <IconButton icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => setSpecimens(prev => prev.filter((_, idx) => idx !== i))} />}
                </HStack>
              ))}
              <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => setSpecimens(prev => [...prev, { quantity: 1, type: '' }])}>+ Add Specimen</Button>

              <Divider my={4} />

              {/* Diagnosis */}
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={3}>Diagnosis</Text>
              {diagnoses.map((dx, i) => (
                <HStack key={i} mb={2}>
                  <Input value={dx} placeholder="ICD-10 code or description" onChange={(e) => { const d = [...diagnoses]; d[i] = e.target.value; setDiagnoses(d); }} />
                  {diagnoses.length > 1 && <IconButton icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => setDiagnoses(prev => prev.filter((_, idx) => idx !== i))} />}
                </HStack>
              ))}
              <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => setDiagnoses(prev => [...prev, ''])}>+ Add Diagnosis</Button>

              <Divider my={4} />

              <FormControl>
                <FormLabel fontSize="sm">Special Instructions</FormLabel>
                <Textarea name="specialInstructions" value={form.specialInstructions} onChange={handleChange} rows={3} placeholder="Fasting required, STAT, etc." />
              </FormControl>

            </Box>
            <Flex justify="space-between" mt={4}>
              <Button variant="outline" onClick={() => setActiveTab(0)}>Back</Button>
              <Button colorScheme="blue" onClick={handleNext}>Next: Documents & Submit</Button>
            </Flex>
          </TabPanel>

          {/* ─── TAB 3: Documents & Submit ────────────────────────── */}
          <TabPanel px={0}>
            <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.100" p={6} mb={4}>
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={4}>Upload Documents</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <DropZone label="Doctor's Script / Prescription" files={scriptFiles} setFiles={setScriptFiles} previews={scriptPreviews} setPreviews={setScriptPreviews} fileNames={scriptFileNames} setFileNames={setScriptFileNames} />
                <DropZone label="Insurance Card" files={insuranceFiles} setFiles={setInsuranceFiles} previews={insurancePreviews} setPreviews={setInsurancePreviews} fileNames={insuranceFileNames} setFileNames={setInsuranceFileNames} />
                <DropZone label="Patient ID" files={idFiles} setFiles={setIdFiles} previews={idPreviews} setPreviews={setIdPreviews} fileNames={idFileNames} setFileNames={setIdFileNames} />
              </SimpleGrid>
            </Box>

            {/* Review Summary */}
            <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.100" p={6}>
              <Text fontWeight="700" fontSize="md" color="blue.700" mb={4}>Review Summary</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} fontSize="sm">
                <Box>
                  <Text fontWeight="600" color="gray.500" mb={1}>Patient</Text>
                  <Text>{form.patientLastName}, {form.patientFirstName}</Text>
                  {form.patientDOB && <Text color="gray.600">DOB: {form.patientDOB}</Text>}
                  {form.patientEmail && <Text color="gray.600">{form.patientEmail}</Text>}
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.500" mb={1}>Insurance</Text>
                  <Text>{form.isSelfPay ? 'Self Pay' : (insurances.find(i => i.id === form.insuranceId)?.name || 'Not selected')}</Text>
                  {form.policyNumber && <Text color="gray.600">Policy: {form.policyNumber}</Text>}
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.500" mb={1}>Tests ({selectedTests.length})</Text>
                  <Flex flexWrap="wrap" gap={1}>
                    {selectedTests.map(t => <Badge key={t.id} colorScheme="blue" fontSize="xs">{t.name}</Badge>)}
                  </Flex>
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.500" mb={1}>Details</Text>
                  {form.bloodCollectionDate && <Text color="gray.600">Date: {form.bloodCollectionDate}</Text>}
                  {selectedPhlebotomist && <Text color="gray.600">Phleb: {selectedPhlebotomist.company_name || selectedPhlebotomist.full_name}</Text>}
                  <Text color="gray.600">Facility: {facility?.name}</Text>
                  <Text color="gray.600">Documents: {scriptFiles.length + insuranceFiles.length + idFiles.length} file(s)</Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Flex justify="space-between" mt={4}>
              <Button variant="outline" onClick={() => setActiveTab(1)}>Back</Button>
              <Button colorScheme="green" size="lg" onClick={handleSubmit} isLoading={submitting} loadingText="Submitting...">
                Submit Order
              </Button>
            </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
